import { Marked, Parser, Renderer, Tokens } from 'marked';
import markedKatex from 'marked-katex-extension';
import { markedTexMath } from './texMathExtension';

function parseInline(this: Renderer, tokens: Tokens.Generic[]): string {
    return (this.parser as Parser).parseInline(tokens);
}

function parseBlock(this: Renderer, tokens: Tokens.Generic[]): string {
    return (this.parser as Parser).parse(tokens);
}

const plainTextRenderer = {
    // --- Block-level Elements ---
    heading(this: Renderer, { tokens, depth }: Tokens.Heading): string {
        const text = parseInline.call(this, tokens).trim();
        return text ? `${'#'.repeat(depth)} ${text}\n\n` : '';
    },

    paragraph(this: Renderer, { tokens }: Tokens.Paragraph): string {
        const text = parseInline.call(this, tokens).trim();
        return text ? `${text}\n\n` : '';
    },

    list(this: Renderer, { ordered, start, items }: Tokens.List): string {
        return `${items
            .map((item, i) => {
                const marker = ordered ? `${(Number(start) || 1) + i}. ` : '- ';
                const inner = parseBlock
                    .call(this, item.tokens)
                    .replace(/\n+$/, '');
                // Indent continuation lines
                const lines = inner.split('\n');
                const indent = ' '.repeat(marker.length);
                return `${marker}${lines
                    .map((l, idx) => (idx === 0 ? l : `${indent}${l}`))
                    .join('\n')}`;
            })
            .join('\n')}\n\n`;
    },

    // listitem is called by the default list handler; we override list entirely,
    // but define listitem as a fallback in case it's invoked standalone.
    listitem(this: Renderer, { tokens }: Tokens.ListItem): string {
        return parseBlock.call(this, tokens).replace(/\n+$/, '');
    },

    blockquote(this: Renderer, { tokens }: Tokens.Blockquote): string {
        const inner = parseBlock.call(this, tokens).replace(/\n+$/, '');
        return `${inner
            .split('\n')
            .map((l) => `> ${l}`)
            .join('\n')}\n\n`;
    },

    code({ text, lang }: Tokens.Code): string {
        const fence = lang ? `\`\`\`${lang}` : '```';
        return `${fence}\n${text}\n\`\`\`\n\n`;
    },

    table(this: Renderer, { header, rows }: Tokens.Table): string {
        const cellText = (cells: Tokens.TableCell[]) =>
            cells.map((c) => parseInline.call(this, c.tokens).trim()).join(' | ');

        const headerRow = cellText(header);
        const separator = header.map(() => '---').join(' | ');
        const bodyRows = rows.map((r) => cellText(r)).join('\n');

        return `${headerRow}\n${separator}\n${bodyRows}\n\n`;
    },

    hr(): string {
        return '---\n\n';
    },

    // --- Inline-level Elements ---
    strong(this: Renderer, { tokens }: Tokens.Strong): string {
        return `**${parseInline.call(this, tokens)}**`;
    },

    em(this: Renderer, { tokens }: Tokens.Em): string {
        return `*${parseInline.call(this, tokens)}*`;
    },

    del(this: Renderer, { tokens }: Tokens.Del): string {
        return `~~${parseInline.call(this, tokens)}~~`;
    },

    codespan({ text }: Tokens.Codespan): string {
        return `\`${text}\``;
    },

    link(this: Renderer, { href, tokens, text }: Tokens.Link): string {
        const label = parseInline.call(this, tokens) || text;
        return href && href !== label ? `${label} (${href})` : label;
    },

    image({ href, text }: Tokens.Image): string {
        return href ? `[${text || ''}](${href})` : text || '';
    },

    br(): string {
        return '\n';
    },

    html(this: Renderer, token: Tokens.HTML | Tokens.Tag): string {
        return token.text;
    },
};

export const plainTextMarked = new Marked({
    renderer: plainTextRenderer as unknown as Renderer,
    useNewRenderer: true,
    gfm: true,
    breaks: false,
});

plainTextMarked.use(markedKatex({ throwOnError: false }));
plainTextMarked.use(markedTexMath());

plainTextMarked.use({
    extensions: [
        {
            name: 'blockKatex',
            level: 'block' as const,
            renderer(token: any): string { return `$$\n${token.text}\n$$\n\n`; },
        },
        {
            name: 'inlineKatex',
            level: 'inline' as const,
            renderer(token: any): string { return `$${token.text}$`; },
        },
    ],
});
